import {ReactElement, ReactNode, useMemo} from 'react';
import {BrowserRouter} from 'react-router-dom';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {I18nextProvider} from 'react-i18next';
import {i18n} from '../configs/i18n/i18n';
import QUERY_CLIENT_CONFIG from '../configs/query.config';
import {QueryClientProvider} from '@tanstack/react-query';
import {PaymentsEnvProvider} from '@chekin/shared/payments';
import {TaxesEnvProvider} from '@chekin/shared/taxes';
import {
  RemoteAccessEnvProvider,
  type ActionableConditionKind,
} from '@chekin/shared/remote-access';
import {SHORT_LINK_TYPE} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useGuestGroup, usePath, usePrivacyPolicyLink, usePaymentsEntryPath} from 'hooks';
import {ChekinSDKProvider, useChekinSDK} from './ChekinSDK';
import {SummaryProvider, useSummary} from './summary';
import {GuestProvider} from './guest';
import {ReservationProvider, useReservation} from './reservation';
import {WebsocketProvider} from './websocket';
import {BrandingThemeProvider} from './brandingTheme';
import {KioskoProvider} from './kiosko';
import {NavigationGuardProvider} from 'context/navigationGuard';
import {resolveConditionPath} from 'components/RemoteAccess/lockConditions';

type AppProvidersProps = {
  children: ReactNode | ReactElement;
};

function PaymentsEnvLayer({children}: {children: ReactNode}) {
  const {isTokenValid} = useChekinSDK();
  const {data: reservation, housing, reservationId} = useReservation();
  const {data: summary} = useSummary();

  const env = useMemo(
    () => ({
      isTokenValid,
      reservationId,
      insuranceSetupId: reservation?.insurance_setup_id,
      isCheckoutReservation: Boolean(housing?.check_out_settings_id),
      isBookingPaymentsActive: summary?.booking_payments?.active,
      isMobileSheetMenuAvailable: true,
    }),
    [
      isTokenValid,
      reservationId,
      reservation?.insurance_setup_id,
      housing?.check_out_settings_id,
      summary?.booking_payments?.active,
    ],
  );

  return <PaymentsEnvProvider value={env}>{children}</PaymentsEnvProvider>;
}

function TaxesEnvLayer({children}: {children: ReactNode}) {
  const {isTokenValid} = useChekinSDK();
  const {housing, reservationId, housingId} = useReservation();
  const {data: summary} = useSummary();
  const {guestGroup, areAllGuestsRegistered} = useGuestGroup();
  const privacyPolicy = usePrivacyPolicyLink({housingId});

  const env = useMemo(
    () => ({
      isTokenValid,
      reservationId,
      housing,
      summary,
      guestGroup,
      areAllGuestsRegistered,
      privacyPolicy: {
        href: privacyPolicy.href,
        isCustomDocument: privacyPolicy.isCustomDocument,
        isLoading: privacyPolicy.isLoading,
      },
    }),
    [
      isTokenValid,
      reservationId,
      housing,
      summary,
      guestGroup,
      areAllGuestsRegistered,
      privacyPolicy.href,
      privacyPolicy.isCustomDocument,
      privacyPolicy.isLoading,
    ],
  );

  return <TaxesEnvProvider value={env}>{children}</TaxesEnvProvider>;
}

function RemoteAccessEnvLayer({children}: {children: ReactNode}) {
  const {isTokenValid, linkType} = useChekinSDK();
  const {data: reservation, housing, reservationId} = useReservation();
  const {data: summary} = useSummary();
  const {totalGuests, totalRegisteredGuests, totalFullyRegisteredGuests} =
    useGuestGroup();
  const {getFullPath, getMainPath} = usePath();
  const paymentsEntryPath = usePaymentsEntryPath();

  const env = useMemo(
    () => ({
      isTokenValid,
      reservationId,
      reservation,
      housing,
      summary,
      guestStats: {totalGuests, totalRegisteredGuests, totalFullyRegisteredGuests},
      isLockLink: linkType === SHORT_LINK_TYPE.lock,
      navigation: {
        conditionPath: (kind: ActionableConditionKind) =>
          resolveConditionPath(kind, {getFullPath, getMainPath, paymentsEntryPath}),
        home: getMainPath(PATHS.home),
      },
    }),
    [
      isTokenValid,
      reservationId,
      reservation,
      housing,
      summary,
      totalGuests,
      totalRegisteredGuests,
      totalFullyRegisteredGuests,
      linkType,
      getFullPath,
      getMainPath,
      paymentsEntryPath,
    ],
  );

  return <RemoteAccessEnvProvider value={env}>{children}</RemoteAccessEnvProvider>;
}

function AppProviders({children = null}: AppProvidersProps) {
  return (
    <QueryClientProvider client={QUERY_CLIENT_CONFIG}>
      <BrowserRouter future={{v7_relativeSplatPath: true, v7_startTransition: true}}>
        <ChekinSDKProvider>
          <I18nextProvider i18n={i18n}>
            <WebsocketProvider>
              <KioskoProvider>
                <BrandingThemeProvider>
                  <ReservationProvider>
                    <SummaryProvider>
                      <PaymentsEnvLayer>
                        <TaxesEnvLayer>
                          <RemoteAccessEnvLayer>
                            <NavigationGuardProvider>
                              <GuestProvider>{children}</GuestProvider>
                            </NavigationGuardProvider>
                          </RemoteAccessEnvLayer>
                        </TaxesEnvLayer>
                      </PaymentsEnvLayer>
                    </SummaryProvider>
                  </ReservationProvider>
                </BrandingThemeProvider>
              </KioskoProvider>
            </WebsocketProvider>
          </I18nextProvider>
        </ChekinSDKProvider>
      </BrowserRouter>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}
export default AppProviders;
